import dayjs from '~/timeInit';

import type { CompetitorProperty, CompetitorPropertyHistoryEntry } from '~/types/services/priceParrot';

import { SALE_DATE_PLACEHOLDER_DAYS, TimelineZoom } from './constants';

const DAY_MS = 24 * 60 * 60 * 1000;
const FULL_RANGE_TICKS = 6;

export interface TimelineRange {
  from: number;
  to: number;
}

// Sale dates only count when they look real; Secret Escapes publishes far-off placeholders.
export function realSaleDate(value: string | undefined, now: number): number | undefined {
  if (!value) {
    return undefined;
  }
  const time = dayjs(value).valueOf();
  return Math.abs(time - now) <= SALE_DATE_PLACEHOLDER_DAYS * DAY_MS ? time : undefined;
}

export interface SupplierTimeline {
  property: CompetitorProperty;
  history: Array<CompetitorPropertyHistoryEntry>;
}

// Every supplier's real sale dates, bookable nights and collections, plus today.
export function fullRange(suppliers: Array<SupplierTimeline>, now: number): TimelineRange {
  const times = [
    now,
    ...suppliers.flatMap(({ property, history }) => [
      realSaleDate(property.saleStart, now),
      realSaleDate(property.saleEnd, now),
      property.firstAvailableNight ? dayjs(property.firstAvailableNight).valueOf() : undefined,
      property.lastAvailableNight ? dayjs(property.lastAvailableNight).valueOf() : undefined,
      ...history.map((entry) => (entry.collectionStartedAt ? dayjs(entry.collectionStartedAt).valueOf() : undefined)),
    ]),
  ].filter((time): time is number => time !== undefined);
  const low = Math.min(...times);
  const high = Math.max(...times);
  const pad = Math.max(2 * DAY_MS, (high - low) * 0.03);
  return {
    from: dayjs(low - pad)
      .startOf('day')
      .valueOf(),
    to: dayjs(high + pad)
      .endOf('day')
      .valueOf(),
  };
}

export function zoomedRange(zoom: TimelineZoom, center: number): TimelineRange {
  const unit = zoom === TimelineZoom.WEEK ? 'week' : 'month';
  const start = dayjs(center).startOf(unit);
  if (zoom === TimelineZoom.QUARTER) {
    const quarterStart = start.month(Math.floor(start.month() / 3) * 3);
    return { from: quarterStart.valueOf(), to: quarterStart.add(3, 'month').valueOf() };
  }
  return { from: start.valueOf(), to: start.add(1, unit).valueOf() };
}

export function shiftCenter(zoom: TimelineZoom, center: number, direction: 1 | -1): number {
  if (zoom === TimelineZoom.WEEK) {
    return dayjs(center).add(direction, 'week').valueOf();
  }
  return dayjs(center)
    .add(direction * (zoom === TimelineZoom.QUARTER ? 3 : 1), 'month')
    .valueOf();
}

export function ticks(zoom: TimelineZoom, range: TimelineRange): Array<number> {
  if (zoom === TimelineZoom.WEEK || zoom === TimelineZoom.MONTH) {
    const step = zoom === TimelineZoom.WEEK ? 1 : 7;
    const values: Array<number> = [];
    for (let day = dayjs(range.from); day.valueOf() < range.to; day = day.add(step, 'day')) {
      values.push(day.valueOf());
    }
    return values;
  }
  const span = range.to - range.from;
  return Array.from({ length: FULL_RANGE_TICKS }, (_, index) =>
    dayjs(range.from + (index * span) / FULL_RANGE_TICKS)
      .startOf('day')
      .valueOf(),
  );
}

export function percentOf(time: number, range: TimelineRange): number {
  return ((time - range.from) / (range.to - range.from)) * 100;
}

export function isInRange(time: number, range: TimelineRange): boolean {
  return time >= range.from && time < range.to;
}
